import { useCallback } from 'react';

import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { useAssetPermissionsCache } from '~/hooks/useAssetPermissionsCache';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useGetPrivateShareLinkMenuOptions } from '~/hooks/useGetShareLinkMenuOptions';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';
import { canViewAsset } from '~/utils/permissions/assetPermissions';
import { canViewBoard } from '~/utils/permissions/boardPermissions';
import { canCreateCollection, canEditCollection } from '~/utils/permissions/workspacePermissions';

export type GetAddToCollectionOptionParams = {
  assetIds: string[];
  boardIds: string[];
};

/**
 * Hook that returns a function to get the "Add to Collection" dropdown menu option.
 * Returns undefined if collections are not enabled or if there are no share link menu options.
 */
export const useAddToCollectionOption = () => {
  const { collectionsEnabled } = useFeatureFlags();
  const { getShareLinkMenuOptions } = useGetPrivateShareLinkMenuOptions();

  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const assetPermissionCache = useAssetPermissionsCache();
  const boardPermissionCache = useBoardPermissionsCache();

  const getAddToCollectionOption = useCallback(
    ({ assetIds, boardIds }: GetAddToCollectionOptionParams): DropdownMenuOption | undefined => {
      const canAddToCollection =
        assetIds.every((assetId) => canViewAsset(assetPermissionCache.getAssetPermissions(assetId))) &&
        boardIds.every((boardId) => canViewBoard(boardPermissionCache.getBoardPermissions(boardId))) &&
        (canEditCollection(workspacePermissions) || canCreateCollection(workspacePermissions));

      const options = getShareLinkMenuOptions({ assetIds, boardIds });

      if (collectionsEnabled && canAddToCollection && options.length > 0) {
        return {
          ...getDropdownMenuOption('add-to-share-link'),
          // Ensures the submenu can expand in height after e.g. displaying no items from a search
          className: 'max-h-[400px]',
          options,
          type: 'sub',
        };
      }
    },
    [collectionsEnabled, getShareLinkMenuOptions, assetPermissionCache, boardPermissionCache, workspacePermissions],
  );

  if (!collectionsEnabled || !getShareLinkMenuOptions) {
    return undefined;
  }

  return getAddToCollectionOption;
};
